import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ShiftStartEndDetails, WorkyTime, PerDayTimeOverride } from './CommonInterfaces';
import { DEFAULT_SHIFT_WALL_CLOCK_ZONE } from './WorkJurisdictionTimezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Interprets calendar + wall-clock fields as a local time in the given IANA zone
 * and returns the corresponding UTC epoch seconds.
 * Do not use setHours + toLocaleString for this — that is wrong when the device
 * zone differs from timeZone.
 */
const convertWallClockToEpochSec = (input: {
    readonly year: number;
    readonly month: number; // 1-12
    readonly day: number;
    readonly hour24: number;
    readonly minute: number;
    readonly timeZone: string;
}): number => {
    const month: string = String(input.month).padStart(2, '0');
    const day: string = String(input.day).padStart(2, '0');
    const hour: string = String(input.hour24).padStart(2, '0');
    const minute: string = String(input.minute).padStart(2, '0');
    const wallClockLocal: string = `${input.year}-${month}-${day} ${hour}:${minute}`;
    return dayjs.tz(wallClockLocal, input.timeZone).unix();
};

/**
 * Converts 12-hour time format to 24-hour format
 * @param time - WorkyTime object with hour (1-12), minute, and isAM flag
 * @returns hour in 24-hour format (0-23)
 */
const convertTo24Hour = (time: WorkyTime): number => {
    if (time.hour === 12) {
        return time.isAM ? 0 : 12; // 12 AM = 0, 12 PM = 12
    }
    return time.isAM ? time.hour : time.hour + 12;
};

const convertWorkyTimeToEpochSec = (
    workyTime: WorkyTime,
    timeZone: string = DEFAULT_SHIFT_WALL_CLOCK_ZONE,
): number => {
    const nowInZone = dayjs().tz(timeZone);
    return convertWallClockToEpochSec({
        year: nowInZone.year(),
        month: nowInZone.month() + 1,
        day: nowInZone.date(),
        hour24: convertTo24Hour(workyTime),
        minute: workyTime.minute,
        timeZone: timeZone,
    });
};

/**
 * Treats the Date's local calendar/time components (getFullYear/getMonth/getDate/
 * getHours/getMinutes) as a wall-clock in timeZone and returns UTC epoch seconds.
 */
const convertDateToEpochSec = (
    dateObj: Date,
    timeZone: string = DEFAULT_SHIFT_WALL_CLOCK_ZONE,
): number => {
    return convertWallClockToEpochSec({
        year: dateObj.getFullYear(),
        month: dateObj.getMonth() + 1,
        day: dateObj.getDate(),
        hour24: dateObj.getHours(),
        minute: dateObj.getMinutes(),
        timeZone: timeZone,
    });
};

const addCalendarDays = (
    year: number,
    month: number,
    day: number,
    daysToAdd: number,
): { readonly year: number; readonly month: number; readonly day: number } => {
    const shifted: Date = new Date(year, month - 1, day + daysToAdd);
    return {
        year: shifted.getFullYear(),
        month: shifted.getMonth() + 1,
        day: shifted.getDate(),
    };
};

/**
 * Uses the explicit end-date offset from the time-step date picker when provided.
 * Does not infer overnight from AM/PM — callers must set the end date for overnight shifts.
 */
const resolveEndDayOffsetDays = (explicitEndDayOffsetDays?: number): number => {
    if (
        explicitEndDayOffsetDays !== undefined &&
        explicitEndDayOffsetDays !== null &&
        Number.isFinite(explicitEndDayOffsetDays) &&
        explicitEndDayOffsetDays >= 0
    ) {
        return Math.floor(explicitEndDayOffsetDays);
    }
    return 0;
};

const calendarDayOffsetBetweenDateKeys = (
    startDateKey: string,
    endDateKey: string,
): number => {
    const startParts: readonly string[] = startDateKey.split('-');
    const endParts: readonly string[] = endDateKey.split('-');
    if (startParts.length !== 3 || endParts.length !== 3) {
        return 0;
    }
    const startDate: Date = new Date(
        Number.parseInt(startParts[0], 10),
        Number.parseInt(startParts[1], 10) - 1,
        Number.parseInt(startParts[2], 10),
    );
    const endDate: Date = new Date(
        Number.parseInt(endParts[0], 10),
        Number.parseInt(endParts[1], 10) - 1,
        Number.parseInt(endParts[2], 10),
    );
    const diffMs: number = endDate.getTime() - startDate.getTime();
    const diffDays: number = Math.round(diffMs / (24 * 60 * 60 * 1000));
    return diffDays < 0 ? 0 : diffDays;
};

const dateKeyFromYearMonthDay = (
    year: number,
    month: number,
    day: number,
): string => {
    const monthStr: string = String(month).padStart(2, '0');
    const dayStr: string = String(day).padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
};

const addDaysToDateKey = (dateKey: string, daysToAdd: number): string => {
    const parts: readonly string[] = dateKey.split('-');
    if (parts.length !== 3) {
        return dateKey;
    }
    const shifted = addCalendarDays(
        Number.parseInt(parts[0], 10),
        Number.parseInt(parts[1], 10),
        Number.parseInt(parts[2], 10),
        daysToAdd,
    );
    return dateKeyFromYearMonthDay(shifted.year, shifted.month, shifted.day);
};

const convertDatesToEpochSec = ({
    dates,
    workyStartTime,
    workyEndTime,
    timeZone = DEFAULT_SHIFT_WALL_CLOCK_ZONE,
    endDayOffsetDays,
}: {
    readonly dates: Date[];
    readonly workyStartTime: WorkyTime;
    readonly workyEndTime: WorkyTime;
    readonly timeZone?: string;
    readonly endDayOffsetDays?: number;
}): ShiftStartEndDetails[] => {
    const startHour24: number = convertTo24Hour(workyStartTime);
    const endHour24: number = convertTo24Hour(workyEndTime);
    const resolvedEndDayOffset: number = resolveEndDayOffsetDays(endDayOffsetDays);

    const epochTimes: ShiftStartEndDetails[] = dates.map((date) => {
        const year: number = date.getFullYear();
        const month: number = date.getMonth() + 1;
        const day: number = date.getDate();
        const endCalendar = addCalendarDays(year, month, day, resolvedEndDayOffset);

        return {
            startDateTimeEpochSec: convertWallClockToEpochSec({
                year,
                month,
                day,
                hour24: startHour24,
                minute: workyStartTime.minute,
                timeZone: timeZone,
            }),
            endDateTimeEpochSec: convertWallClockToEpochSec({
                year: endCalendar.year,
                month: endCalendar.month,
                day: endCalendar.day,
                hour24: endHour24,
                minute: workyEndTime.minute,
                timeZone: timeZone,
            }),
        };
    });

    return epochTimes;
};

const epochToDate = (epochTimeInSeconds: number): string => {
    const date: Date = new Date(epochTimeInSeconds * 1000); // Convert seconds to milliseconds
    const year: number = date.getFullYear();
    const month: string = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day: string = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Extracts calendar date + wall-clock hour/minute for an instant in the given IANA zone.
 * Use this instead of Date.getHours() when the shift timezone may differ from the browser zone.
 */
const getWallClockPartsFromEpochSec = (input: {
    readonly epochSec: number;
    readonly timeZone: string;
}): {
    readonly year: number;
    readonly month: number;
    readonly day: number;
    readonly hour24: number;
    readonly minute: number;
    readonly dateKey: string;
} => {
    const zoned = dayjs.unix(input.epochSec).tz(input.timeZone);
    return {
        year: zoned.year(),
        month: zoned.month() + 1,
        day: zoned.date(),
        hour24: zoned.hour(),
        minute: zoned.minute(),
        dateKey: zoned.format('YYYY-MM-DD'),
    };
};

const convertDateEpochAndTimeToEpochSec = ({
    epochTimeInSeconds,
    hours,
    minutes,
}: {
    readonly epochTimeInSeconds: number;
    readonly hours: number;
    readonly minutes: number;
}): number => {
    const date: Date = new Date(epochToDate(epochTimeInSeconds));
    date.setHours(hours, minutes, 0, 0); // Set hours, minutes, seconds, and milliseconds
    return Math.floor(date.getTime() / 1000); // Convert to epoch seconds
};

const convertDateStringAndTimeToEpochSec = (
    dateString: string,
    hours: number,
    minutes: number,
): number => {
    const date: Date = new Date(dateString);
    date.setHours(hours, minutes, 0, 0); // Set hours, minutes, seconds, and milliseconds
    return Math.floor(date.getTime() / 1000); // Convert to epoch seconds
};

/**
 * Converts seconds to minutes, defaulting to 0 when the value is null or undefined
 * @param seconds - The number of seconds to convert, or null/undefined
 * @returns The number of minutes (seconds / 60), or 0 if input is null/undefined
 */
const convertSecondsToMinutes = (seconds: number | null | undefined): number => {
    try {
        if (seconds === null || seconds === undefined) {
            return 0;
        }
        return seconds / 60;
    } catch (e) {
        console.error('Error converting seconds to minutes:', e);
        return 0;
    }
};

/**
 * Converts seconds to hours, defaulting to 0 when the value is null or undefined
 * @param seconds - The number of seconds to convert, or null/undefined
 * @returns The number of hours (seconds / 3600), or 0 if input is null/undefined
 */
const convertSecondsToHours = (seconds: number | null | undefined): number => {
    try {
        if (seconds === null || seconds === undefined) {
            return 0;
        }
        return seconds / 3600;
    } catch (e) {
        console.error('Error converting seconds to hours:', e);
        return 0;
    }
};

/**
 * Formats a duration in whole seconds for UI display (e.g. billing deduction overrides).
 * @param totalSeconds — Non-negative duration in seconds
 * @returns Human-readable string such as "45 sec", "12 min", or "12 min 30 sec"
 */
const formatDurationSecondsForDisplay = (totalSeconds: number): string => {
    const whole: number = Math.floor(Math.max(0, totalSeconds));
    if (whole < 60) {
        return `${whole} sec`;
    }
    const minutes: number = Math.floor(whole / 60);
    const secondsRemainder: number = whole % 60;
    if (secondsRemainder === 0) {
        return `${minutes} min`;
    }
    return `${minutes} min ${secondsRemainder} sec`;
};

/**
 * Builds a decimal-minutes string from a duration in seconds (e.g. for number inputs).
 * Whole minutes omit decimals; otherwise up to four decimal places.
 */
const deductionMinutesStringFromSeconds = (totalSeconds: number): string => {
    const minutes: number = totalSeconds / 60;
    if (!Number.isFinite(minutes)) {
        return '';
    }
    if (Number.isInteger(minutes)) {
        return String(minutes);
    }
    return String(parseFloat(minutes.toFixed(4)));
};

/**
 * Formats a Date object to an ISO date key string (YYYY-MM-DD) for use as a map key in per-day overrides.
 */
const formatDateToDateKey = (date: Date): string => {
    if (!date) {
        return '';
    }
    const year: number = date.getFullYear();
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formats Unix epoch seconds for display in read-only UI (e.g. shift note timestamps).
 * V2: locale-aware date + short time.
 * When timeZone is omitted, uses the runtime default locale/zone (historical behavior).
 * When timeZone is provided, formats the wall-clock in that IANA zone.
 */
const formatEpochSecondsForDisplayV2 = (
    epochSeconds: number,
    timeZone?: string,
): string => {
    const date: Date = new Date(epochSeconds * 1000);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    return date.toLocaleString(undefined, options);
};

/**
 * Formats epoch seconds like convertEpochToHumanFriendlyDateV2 (`yyyy-mm-dd h:mm AM/PM TZ`)
 * but in an explicit IANA zone (e.g. America/Edmonton → MDT).
 */
const formatEpochToHumanFriendlyInTimeZone = (
    epochSeconds: number,
    timeZone: string,
): string => {
    try {
        const date: Date = new Date(epochSeconds * 1000);
        if (Number.isNaN(date.getTime()) || !timeZone) {
            return '';
        }
        const formatted: string = date.toLocaleString('en-US', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        });
        const parts: string[] = formatted.split(', ');
        if (parts.length === 2) {
            const datePart: string = parts[0];
            const timeAndTz: string = parts[1];
            const [month, day, year] = datePart.split('/');
            const dateStr: string = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            return `${dateStr} ${timeAndTz}`;
        }
        return formatted;
    } catch (e) {
        console.error('Error formatting epoch in time zone:', epochSeconds, timeZone, e);
        return '';
    }
};

/**
 * Builds a `datetime-local` input value (`YYYY-MM-DDTHH:mm`) from an instant
 * projected into the given IANA zone. Prefer this over Date.getHours() when the
 * display zone may differ from the browser zone.
 */
const convertEpochToDateTimeLocalInTimeZone = (
    epochSeconds: number,
    timeZone: string,
): string => {
    const parts = getWallClockPartsFromEpochSec({
        epochSec: epochSeconds,
        timeZone: timeZone,
    });
    const month: string = String(parts.month).padStart(2, '0');
    const day: string = String(parts.day).padStart(2, '0');
    const hour: string = String(parts.hour24).padStart(2, '0');
    const minute: string = String(parts.minute).padStart(2, '0');
    return `${parts.year}-${month}-${day}T${hour}:${minute}`;
};

/**
 * Interprets a `datetime-local` string (`YYYY-MM-DDTHH:mm` or with seconds) as a
 * wall-clock in the given IANA zone and returns UTC epoch seconds.
 */
const convertDateTimeLocalToEpochInTimeZone = (
    dateTimeLocal: string,
    timeZone: string,
): number | null => {
    if (!dateTimeLocal) {
        return null;
    }
    const match: RegExpExecArray | null = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(
        dateTimeLocal,
    );
    if (!match) {
        return null;
    }
    return convertWallClockToEpochSec({
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour24: Number(match[4]),
        minute: Number(match[5]),
        timeZone: timeZone,
    });
};

/**
 * Formats Unix epoch seconds as a UI-friendly datetime: `yyyy-mm-dd hh:mm AM/PM`.
 * Null, undefined, non-positive, or invalid values return an empty string.
 * When timeZone is omitted, uses the device-local zone (historical behavior).
 * When timeZone is provided, uses that IANA zone for the wall-clock parts.
 */
const convertEpochSecToUIFriendly = (
    epochSeconds: number | null | undefined,
    timeZone?: string,
): string => {
    if (epochSeconds == null || epochSeconds <= 0) {
        return '';
    }
    const date: Date = new Date(epochSeconds * 1000);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    if (timeZone) {
        const parts: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).formatToParts(date);

        const getPart = (partType: Intl.DateTimeFormatPartTypes): string => {
            const part: Intl.DateTimeFormatPart | undefined = parts.find(
                (candidate) => candidate.type === partType,
            );
            return part?.value ?? '';
        };

        const year: string = getPart('year');
        const month: string = getPart('month');
        const day: string = getPart('day');
        const hourRaw: string = getPart('hour');
        const minute: string = getPart('minute');
        const dayPeriod: string = getPart('dayPeriod').toUpperCase();
        const hour: string = hourRaw.padStart(2, '0');
        return `${year}-${month}-${day} ${hour}:${minute} ${dayPeriod}`;
    }

    const year: string = String(date.getFullYear());
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');
    const hours24: number = date.getHours();
    const isAm: boolean = hours24 < 12;
    const hours12: number = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const hour: string = String(hours12).padStart(2, '0');
    const minute: string = String(date.getMinutes()).padStart(2, '0');
    const amPm: string = isAm ? 'AM' : 'PM';
    return `${year}-${month}-${day} ${hour}:${minute} ${amPm}`;
};

/**
 * Converts dates to epoch seconds using per-day time overrides when available,
 * falling back to the default start/end times.
 */
const convertDatesToEpochSecWithPerDayTimes = ({
    dates,
    defaultStartTime,
    defaultEndTime,
    perDayTimeOverrides,
    timeZone = DEFAULT_SHIFT_WALL_CLOCK_ZONE,
    defaultEndDayOffsetDays,
}: {
    readonly dates: Date[];
    readonly defaultStartTime: WorkyTime;
    readonly defaultEndTime: WorkyTime;
    readonly perDayTimeOverrides: readonly PerDayTimeOverride[];
    readonly timeZone?: string;
    readonly defaultEndDayOffsetDays?: number;
}): ShiftStartEndDetails[] => {
    const overrideMap: Map<string, PerDayTimeOverride> = new Map<string, PerDayTimeOverride>();

    for (const override of perDayTimeOverrides) {
        overrideMap.set(override.dateKey, override);
    }

    const epochTimes: ShiftStartEndDetails[] = dates.map((date) => {
        const dateKey: string = formatDateToDateKey(date);
        const override: PerDayTimeOverride | undefined = overrideMap.get(dateKey);
        const startTime: WorkyTime = override?.startTime ?? defaultStartTime;
        const endTime: WorkyTime = override?.endTime ?? defaultEndTime;

        const startHour24: number = convertTo24Hour(startTime);
        const endHour24: number = convertTo24Hour(endTime);
        const year: number = date.getFullYear();
        const month: number = date.getMonth() + 1;
        const day: number = date.getDate();
        const resolvedEndDayOffset: number = resolveEndDayOffsetDays(
            override?.endDayOffsetDays !== undefined
                ? override.endDayOffsetDays
                : defaultEndDayOffsetDays,
        );
        const endCalendar = addCalendarDays(year, month, day, resolvedEndDayOffset);

        return {
            startDateTimeEpochSec: convertWallClockToEpochSec({
                year,
                month,
                day,
                hour24: startHour24,
                minute: startTime.minute,
                timeZone: timeZone,
            }),
            endDateTimeEpochSec: convertWallClockToEpochSec({
                year: endCalendar.year,
                month: endCalendar.month,
                day: endCalendar.day,
                hour24: endHour24,
                minute: endTime.minute,
                timeZone: timeZone,
            }),
        };
    });

    return epochTimes;
};

export {
    DEFAULT_SHIFT_WALL_CLOCK_ZONE,
    epochToDate,
    getWallClockPartsFromEpochSec,
    convertWallClockToEpochSec,
    convertWorkyTimeToEpochSec,
    convertDateToEpochSec,
    convertDatesToEpochSec,
    convertDateEpochAndTimeToEpochSec,
    convertSecondsToMinutes,
    convertSecondsToHours,
    formatDurationSecondsForDisplay,
    deductionMinutesStringFromSeconds,
    convertDatesToEpochSecWithPerDayTimes,
    formatDateToDateKey,
    formatEpochSecondsForDisplayV2,
    formatEpochToHumanFriendlyInTimeZone,
    convertEpochToDateTimeLocalInTimeZone,
    convertDateTimeLocalToEpochInTimeZone,
    convertEpochSecToUIFriendly,
    resolveEndDayOffsetDays,
    calendarDayOffsetBetweenDateKeys,
    addDaysToDateKey,
    addCalendarDays,
};
